import {useRouter} from 'next/router';
import {useCallback, useContext} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import useUserActions from '@/src/app/store/user/useUserActions';
import useLoading from '@/src/hooks/useLoading';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import PopupContext from '@/src/popup/utils/PopupContext';
import EditAcademicTitlePopupLayout from '@/src/profile/accountSettings/components/EditAcademicTitlePopupLayout';
import updateAcademicTitles from '@/src/profile/accountSettings/utils/updateAcademicTitles';
import fetchUserAction from '@/src/signUp/paymentDetails/utils/fetchUserAction';

interface EditAcademicTitlePopupProps {
  prefixedAcademicTitle?: string | null;
  suffixedAcademicTitle?: string | null;
}

const EditAcademicTitlePopup = ({
  prefixedAcademicTitle,
  suffixedAcademicTitle,
}: EditAcademicTitlePopupProps) => {
  const {closePopup} = useContext(PopupContext);
  const {isLoading, setLoading} = useLoading();
  const {setUserData} = useUserActions();
  const {push} = useRouter();
  // @ts-ignore
  const {SIGN_IN_ROUTE} = useRoutePaths();

  const fetchUserData = useCallback(async () => {
    const {status: fetchUserStatus, responseBody} = await fetchUserAction();

    if (fetchUserStatus === RESPONSE_STATUSES.SUCCESS) {
      setUserData(responseBody);
    } else {
      push(SIGN_IN_ROUTE.getLink());
    }
  }, [push]);

  const handleSubmitClick = useCallback(async (values, {setFieldError}) => {
    setLoading(true);

    const {responseBody, status} = await updateAcademicTitles(
      values.prefixed_academic_title,
      values.suffixed_academic_title
    );

    if (status === RESPONSE_STATUSES.SUCCESS) {
      await fetchUserData();

      closePopup();
    }

    if (
      responseBody &&
      responseBody.error &&
      typeof responseBody.error === 'object'
    ) {
      for (const key in responseBody.error) {
        if (Object.prototype.hasOwnProperty.call(responseBody.error, key)) {
          setFieldError(key, responseBody.error[key]);
        }
      }
    }

    setLoading(false);
  }, []);

  const handleCancelClick = useCallback(() => {
    closePopup();
  }, []);

  return (
    <EditAcademicTitlePopupLayout
      isLoading={isLoading}
      onCancelClick={handleCancelClick}
      onSubmitClick={handleSubmitClick}
      prefixedAcademicTitle={prefixedAcademicTitle}
      suffixedAcademicTitle={suffixedAcademicTitle}
    />
  );
};

export default EditAcademicTitlePopup;
